import { useState, useCallback, useMemo } from 'react'
import { Plus, Shield, Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/data/DataTable'
import { RoleManagementDialog } from '../components/RoleManagementDialog'
import { RoleDeleteDialog } from '../components/RoleDeleteDialog'
import { useRoles, useDeleteRole } from '../hooks'
import { useDatabaseTranslationRecords } from '@/hooks/useDatabaseTranslation'
import { TRANSLATION_SCOPES } from '@/utils/databaseTranslation'
import { createRoleColumns } from '../columns'
import type { Role } from '@/types/entities'

export function RoleListPage() {
    const { t } = useTranslation()
    const [selectedRole, setSelectedRole] = useState<Role | null>(null)
    const [showCreateDialog, setShowCreateDialog] = useState(false)
    const [showEditDialog, setShowEditDialog] = useState(false)
    const [showDeleteDialog, setShowDeleteDialog] = useState(false)
    const [showViewDialog, setShowViewDialog] = useState(false)

    const { data: rolesResponse, isLoading } = useRoles()
    const deleteRole = useDeleteRole()

    const roles = rolesResponse?.data || []
    const { data: displayRoles } = useDatabaseTranslationRecords<Role>(
        roles,
        TRANSLATION_SCOPES.ROLE,
        { sourceLanguage: 'en', autoTranslate: true }
    )

    // Memoize callbacks to prevent re-creating on every render
    const handleEdit = useCallback((role: Role) => {
        setSelectedRole(role)
        setShowEditDialog(true)
    }, [])

    const handleView = useCallback((role: Role) => {
        setSelectedRole(role)
        setShowViewDialog(true)
    }, [])

    const handleDelete = useCallback((role: Role) => {
        setSelectedRole(role)
        setShowDeleteDialog(true)
    }, [])

    const handleDeleteConfirm = useCallback(() => {
        if (selectedRole) {
            deleteRole.mutate(selectedRole.id, {
                onSuccess: () => {
                    setShowDeleteDialog(false)
                    setSelectedRole(null)
                },
            })
        }
    }, [selectedRole, deleteRole])

    const handleExport = useCallback(() => {
        // TODO: Implement CSV export
        console.log('Export roles to CSV')
    }, [])

    // Memoize columns to prevent re-creation
    const columns = useMemo(
        () => createRoleColumns({
            onEdit: handleEdit,
            onDelete: handleDelete,
            onView: handleView,
            t: t,
        }),
        [handleEdit, handleDelete, handleView, t]
    )

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-muted-foreground">{t('roles.loadingRoles')}</div>
            </div>
        )
    }

    return (
        <div className="space-y-[var(--spacing-section)]" style={{ padding: 'var(--spacing-page)' }}>
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-xl lg:text-2xl font-bold">{t('roles.title')}</h1>
                    <p className="text-muted-foreground">
                        {t('roles.description')}
                    </p>
                </div>
                <div className="flex space-x-2">
                    <Button onClick={() => setShowCreateDialog(true)}>
                        <Plus className="mr-2 h-4 w-4" />
                        {t('roles.createRole')}
                    </Button>
                </div>
            </div>

            {/* Stats */}
            <div className="grid gap-4 md:grid-cols-3">
                <div className="rounded-lg border bg-card p-6">
                    <div className="flex items-center space-x-2">
                        <Shield className="h-5 w-5 text-muted-foreground" />
                        <div>
                            <p className="text-sm font-medium">{t('roles.totalRoles')}</p>
                            <p className="text-2xl font-bold">{roles.length}</p>
                        </div>
                    </div>
                </div>
                <div className="rounded-lg border bg-card p-6">
                    <div className="flex items-center space-x-2">
                        <Shield className="h-5 w-5 text-muted-foreground" />
                        <div>
                            <p className="text-sm font-medium">{t('roles.systemRoles')}</p>
                            <p className="text-2xl font-bold">
                                {roles.filter(role => ['admin', 'user', 'viewer'].includes(role.name)).length}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="rounded-lg border bg-card p-6">
                    <div className="flex items-center space-x-2">
                        <Users className="h-5 w-5 text-muted-foreground" />
                        <div>
                            <p className="text-sm font-medium">{t('roles.customRoles')}</p>
                            <p className="text-2xl font-bold">
                                {roles.filter(role => !['admin', 'user', 'viewer'].includes(role.name)).length}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Table */}
            <DataTable
                columns={columns}
                data={displayRoles}
                searchKey="name"
                searchPlaceholder={t('roles.searchPlaceholder')}
                onExport={handleExport}
                exportButtonText={t('roles.export')}
                enableRowSelection={false}
            />

            {/* Dialogs */}
            <RoleManagementDialog
                open={showCreateDialog}
                onOpenChange={setShowCreateDialog}
                role={null}
                mode="create"
            />

            <RoleManagementDialog
                open={showEditDialog}
                onOpenChange={setShowEditDialog}
                role={selectedRole}
                mode="edit"
            />

            <RoleManagementDialog
                open={showViewDialog}
                onOpenChange={setShowViewDialog}
                role={selectedRole}
                mode="view"
            />

            <RoleDeleteDialog
                open={showDeleteDialog}
                onOpenChange={setShowDeleteDialog}
                role={selectedRole}
                onConfirm={handleDeleteConfirm}
                isLoading={deleteRole.isPending}
            />
        </div>
    )
}










